import { IconChevronLeftFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-left-filled';
import { IconChevronRightFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-right-filled';
import { Fragment, forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { isChildrenEmpty } from '../../utils/is-children-empty';
import { IconButton } from '../icon-button';
import { CarouselRootContext } from './context/carousel-root.context';
import { carouselNavControlsStyles } from './styles/carousel-nav-controls.styles';
import type { CarouselNavControlsPropsInterface } from './types/carousel-nav-controls.types';
import type { CarouselRootContextInterface } from './types/carousel-root.types';

export const CarouselNavControls = forwardRef<HTMLDivElement, CarouselNavControlsPropsInterface>(
  (props: CarouselNavControlsPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, leftButtonProps, rightButtonProps } = props;

    const { carousel } = useContext<CarouselRootContextInterface>(CarouselRootContext);

    const styles = css(carouselNavControlsStyles, cssProp);

    return (
      <div
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Carousel.NavControls"
        className={cx('c11n', 'c11n_carousel__nav-controls', styles, className)}
      >
        {isChildrenEmpty(children) ? (
          <Fragment>
            <IconButton
              shape="circle"
              aria-label="Previous"
              icon={<IconChevronLeftFilled />}
              size="md"
              tabIndex={-1}
              title="Previous items"
              onClick={carousel?.previousPage}
              disabled={!carousel?.previousEnabled}
              data-c11n-version={__C11N_VERSION__}
              data-c11n-component="Carousel.PreviousButton"
              {...leftButtonProps}
              className={cx('c11n_carousel__previous-button', leftButtonProps?.className)}
            />
            <IconButton
              shape="circle"
              aria-label="Next"
              icon={<IconChevronRightFilled />}
              size="md"
              tabIndex={-1}
              title="Next items"
              onClick={carousel?.nextPage}
              disabled={!carousel?.nextEnabled}
              data-c11n-version={__C11N_VERSION__}
              data-c11n-component="Carousel.NextButton"
              {...rightButtonProps}
              className={cx('c11n_carousel__next-button', rightButtonProps?.className)}
            />
          </Fragment>
        ) : (
          children
        )}
      </div>
    );
  },
);

CarouselNavControls.displayName = 'Carousel.NavControls';
